import { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { X, ChevronLeft, ChevronRight, ZoomIn, Box, Image as ImageIcon, TrendingDown } from 'lucide-react';
import { getImageUrl, getTypeLabel } from '../api/plvApi';
import type { PLVProduct } from '../types/plv.types';
import { getPricingInfo } from '../utils/pricingUtils';
import { PricingTiersModal } from './PricingTiersModal';

const Brochure3D = lazy(() => import('./Brochure3D').then((m) => ({ default: m.Brochure3D })));

interface ProductModalProps {
  product: PLVProduct;
  onClose: () => void;
}

export function ProductModal({ product, onClose }: ProductModalProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [fullscreenIndex, setFullscreenIndex] = useState<number | null>(null);
  const [showPricingModal, setShowPricingModal] = useState(false);

  // Get print file images (without presentation image)
  const getAllImages = (): string[] => {
    const images: string[] = [];
    if (product.files?.printFiles) {
      [...product.files.printFiles]
        .sort((a, b) => a.order - b.order)
        .forEach(file => { if (file.url) images.push(file.url); });
    }
    if (images.length === 0 && product.images) {
      images.push(...product.images);
    }
    return images;
  };

  const allImages = getAllImages();

  // 3D viewer detection
  const hasBrochure3D = (): boolean => {
    const processingType = product.files?.metadata?.processingType;
    return Boolean(
      (processingType === 'pdf_split' || processingType === 'pdf_card') &&
      product.files?.printFiles &&
      product.files.printFiles.length >= 2,
    );
  };

  const getBrochure3DType = (): 'brochure' | 'card' => {
    return product.files?.metadata?.processingType === 'pdf_card' ? 'card' : 'brochure';
  };

  const getBrochure3DImages = (): string[] => {
    if (!hasBrochure3D() || !product.files?.printFiles) return [];
    const type = getBrochure3DType();
    const expectedCount = type === 'card' ? 2 : 4;
    const sortedFiles = [...product.files.printFiles].sort((a, b) => a.order - b.order);
    const images = sortedFiles.slice(0, expectedCount).map(file => file.url);
    while (images.length < expectedCount && sortedFiles.length > 0) {
      images.push(sortedFiles[images.length % sortedFiles.length].url);
    }
    return images.slice(0, expectedCount);
  };

  // Slides: 3D viewer (if available) + images
  const has3D = hasBrochure3D();
  const totalSlides = allImages.length + (has3D ? 1 : 0);
  const isSlide3D = (index: number) => has3D && index === 0;
  const getImageForSlide = (index: number) => allImages[has3D ? index - 1 : index];

  // Navigation
  const goToSlide = (index: number) => {
    if (index >= 0 && index < totalSlides) setActiveIndex(index);
  };

  const prevSlide = () => goToSlide(activeIndex > 0 ? activeIndex - 1 : totalSlides - 1);
  const nextSlide = () => goToSlide(activeIndex < totalSlides - 1 ? activeIndex + 1 : 0);

  // Fullscreen
  const openFullscreen = (index: number) => setFullscreenIndex(index);
  const closeFullscreen = () => setFullscreenIndex(null);

  const fullscreenPrev = useCallback(() => {
    if (fullscreenIndex === null) return;
    const prev = fullscreenIndex > 0 ? fullscreenIndex - 1 : totalSlides - 1;
    setFullscreenIndex(prev);
  }, [fullscreenIndex, totalSlides]);

  const fullscreenNext = useCallback(() => {
    if (fullscreenIndex === null) return;
    const next = fullscreenIndex < totalSlides - 1 ? fullscreenIndex + 1 : 0;
    setFullscreenIndex(next);
  }, [fullscreenIndex, totalSlides]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (showPricingModal) return;
      if (e.key === 'Escape') {
        if (fullscreenIndex !== null) closeFullscreen();
        else onClose();
      }
      if (e.key === 'ArrowLeft') {
        if (fullscreenIndex !== null) fullscreenPrev();
        else prevSlide();
      }
      if (e.key === 'ArrowRight') {
        if (fullscreenIndex !== null) fullscreenNext();
        else nextSlide();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [fullscreenIndex, showPricingModal, fullscreenPrev, fullscreenNext]);

  const pricingInfo = getPricingInfo(product);

  // Filmstrip items
  interface FilmstripItem {
    type: '3d' | 'image';
    slideIndex: number;
    thumbnail: string | null;
    label: string;
  }

  const filmstripItems: FilmstripItem[] = [];
  if (has3D) {
    filmstripItems.push({ type: '3d', slideIndex: 0, thumbnail: null, label: 'Visionneuse 3D' });
  }
  allImages.forEach((url, i) => {
    filmstripItems.push({
      type: 'image',
      slideIndex: has3D ? i + 1 : i,
      thumbnail: url,
      label: `Image ${i + 1}`,
    });
  });

  return (
    <>
      {/* Main modal */}
      <div
        className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-bg-black/50"
        onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      >
        <div
          className="tw-bg-white tw-rounded-xl tw-shadow-2xl tw-w-full tw-max-w-6xl tw-mx-4 tw-max-h-[95vh] tw-overflow-hidden tw-flex tw-flex-col"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="tw-flex tw-items-center tw-justify-between tw-px-6 tw-py-3 tw-border-b tw-border-gray-200">
            <h5 className="tw-text-base tw-font-semibold tw-flex tw-items-center tw-gap-2">
              {product.name}
            </h5>
            <button
              className="tw-p-1 tw-rounded hover:tw-bg-gray-100 tw-transition-colors tw-text-gray-400 hover:tw-text-gray-600"
              onClick={onClose}
            >
              <X className="tw-w-5 tw-h-5" />
            </button>
          </div>

          {/* Body */}
          <div className="tw-flex-1 tw-overflow-hidden">
            {allImages.length > 0 || has3D ? (
              <div className="tw-flex tw-flex-col lg:tw-flex-row tw-h-full">
                {/* Image viewer - left */}
                <div className="tw-flex-1 tw-relative tw-min-h-[300px] lg:tw-min-h-[500px]">
                  {/* Active slide */}
                  <div className="tw-w-full tw-h-full tw-flex tw-items-center tw-justify-center tw-bg-white tw-relative">
                    {isSlide3D(activeIndex) ? (
                      <div className="tw-w-full tw-h-full" style={{ minHeight: '500px' }}>
                        <Suspense
                          fallback={
                            <div className="tw-w-full tw-h-full tw-flex tw-items-center tw-justify-center tw-text-sm tw-text-gray-500" style={{ minHeight: '500px' }}>
                              Chargement de la visionneuse 3D…
                            </div>
                          }
                        >
                          <Brochure3D
                            images={getBrochure3DImages()}
                            displayControls={true}
                            isFullscreen={false}
                            type={getBrochure3DType()}
                            onFullscreenRequest={() => openFullscreen(activeIndex)}
                            productName={product.name}
                          />
                        </Suspense>
                      </div>
                    ) : (
                      <div
                        className="tw-w-full tw-h-full tw-flex tw-items-center tw-justify-center tw-cursor-zoom-in tw-p-4"
                        style={{ minHeight: '500px' }}
                        onClick={() => openFullscreen(activeIndex)}
                      >
                        <img
                          src={(() => {
                            const url = getImageForSlide(activeIndex);
                            return url?.startsWith('http') ? url : getImageUrl(url || '');
                          })()}
                          alt={`${product.name} - ${activeIndex + 1}`}
                          className="tw-max-h-[480px] tw-max-w-full tw-object-contain tw-rounded"
                        />
                        {/* Zoom hint */}
                        <span className="tw-absolute tw-bottom-4 tw-left-1/2 -tw-translate-x-1/2 tw-inline-flex tw-items-center tw-gap-1 tw-px-3 tw-py-1 tw-text-xs tw-font-medium tw-rounded tw-bg-black/60 tw-text-white">
                          <ZoomIn className="tw-w-3.5 tw-h-3.5" />
                          Cliquer pour agrandir
                        </span>
                      </div>
                    )}

                    {/* Slide counter */}
                    <span className="tw-absolute tw-top-3 tw-right-3 tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-rounded tw-bg-black/60 tw-text-white">
                      {activeIndex + 1} / {totalSlides}
                    </span>

                    {/* Navigation arrows */}
                    {totalSlides > 1 && (
                      <>
                        <button
                          className="tw-absolute tw-left-3 tw-top-1/2 -tw-translate-y-1/2 tw-p-2 tw-rounded-full tw-bg-white tw-shadow-md tw-border tw-border-gray-200 tw-text-gray-600 hover:tw-bg-gray-50 tw-transition-colors tw-z-10"
                          onClick={e => { e.stopPropagation(); prevSlide(); }}
                        >
                          <ChevronLeft className="tw-w-5 tw-h-5" />
                        </button>
                        <button
                          className="tw-absolute tw-right-3 tw-top-1/2 -tw-translate-y-1/2 tw-p-2 tw-rounded-full tw-bg-white tw-shadow-md tw-border tw-border-gray-200 tw-text-gray-600 hover:tw-bg-gray-50 tw-transition-colors tw-z-10"
                          onClick={e => { e.stopPropagation(); nextSlide(); }}
                        >
                          <ChevronRight className="tw-w-5 tw-h-5" />
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Description - right */}
                <div className="lg:tw-w-80 tw-border-t lg:tw-border-t-0 lg:tw-border-l tw-border-gray-200 tw-p-5 tw-flex tw-flex-col">
                  <div className="tw-mb-4">
                    <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
                      <h6 className="tw-text-sm tw-font-semibold">{product.name}</h6>
                      <span className="tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-rounded tw-bg-gray-100 tw-text-gray-600">
                        {getTypeLabel(product.type)}
                      </span>
                    </div>
                    {product.description && (
                      <p className="tw-text-sm tw-text-gray-500 tw-whitespace-pre-wrap tw-leading-relaxed">
                        {product.description}
                      </p>
                    )}
                  </div>
                  <div className="tw-mt-auto">
                    {pricingInfo.hasMultiplePairs && (
                      <button
                        className="tw-w-full tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-rounded-lg tw-border tw-border-affinicar-secondary tw-text-affinicar-secondary hover:tw-bg-affinicar-lighter tw-transition-colors"
                        onClick={() => setShowPricingModal(true)}
                      >
                        <TrendingDown className="tw-w-4 tw-h-4" />
                        Prix par quantité
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-12 tw-text-gray-400">
                <ImageIcon className="tw-w-16 tw-h-16 tw-mb-3" />
                <p className="tw-text-sm">Aucune image disponible</p>
              </div>
            )}
          </div>

          {/* Filmstrip */}
          {filmstripItems.length > 1 && (
            <div className="tw-bg-gray-50 tw-border-t tw-border-gray-200 tw-p-3 tw-flex tw-justify-center tw-rounded-b-xl">
              <div className="tw-flex tw-gap-2 tw-flex-wrap tw-justify-center">
                {filmstripItems.map(item => (
                  <button
                    key={`filmstrip-${item.slideIndex}`}
                    className={`tw-w-20 tw-h-[60px] tw-rounded-lg tw-border-2 tw-overflow-hidden tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-transition-all tw-relative ${
                      activeIndex === item.slideIndex
                        ? 'tw-border-affinicar-secondary tw-shadow-md tw-scale-110'
                        : 'tw-border-gray-200 tw-bg-white hover:tw-border-gray-400 hover:tw-scale-105'
                    }`}
                    onClick={() => goToSlide(item.slideIndex)}
                    title={item.label}
                  >
                    {item.type === '3d' ? (
                      <div className="tw-text-center">
                        <Box className={`tw-w-5 tw-h-5 tw-mx-auto ${activeIndex === item.slideIndex ? 'tw-text-affinicar-secondary' : 'tw-text-gray-500'}`} />
                        <span className={`tw-text-[10px] tw-font-bold ${activeIndex === item.slideIndex ? 'tw-text-affinicar-secondary' : 'tw-text-gray-500'}`}>3D</span>
                      </div>
                    ) : (
                      <img
                        src={item.thumbnail?.startsWith('http') ? item.thumbnail : getImageUrl(item.thumbnail || '')}
                        alt={item.label}
                        className="tw-w-full tw-h-full tw-object-cover"
                        onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                      />
                    )}
                    <span className="tw-absolute tw-bottom-0.5 tw-right-0.5 tw-bg-black/70 tw-text-white tw-text-[9px] tw-px-1 tw-rounded tw-font-bold tw-leading-none">
                      {item.slideIndex + 1}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Fullscreen viewer */}
      {fullscreenIndex !== null && (
        <div
          className="tw-fixed tw-inset-0 tw-z-[60] tw-flex tw-items-center tw-justify-center tw-bg-white/95"
          style={{ cursor: isSlide3D(fullscreenIndex) ? 'default' : 'zoom-out' }}
          onClick={e => { if (e.target === e.currentTarget) closeFullscreen(); }}
        >
          <button
            className="tw-absolute tw-top-4 tw-right-4 tw-p-2 tw-rounded-full tw-bg-gray-200 hover:tw-bg-gray-300 tw-text-gray-700 tw-transition-colors tw-z-10"
            onClick={closeFullscreen}
          >
            <X className="tw-w-5 tw-h-5" />
          </button>

          {/* Slide counter */}
          <span className="tw-absolute tw-top-4 tw-left-1/2 -tw-translate-x-1/2 tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium tw-rounded tw-bg-gray-800 tw-text-white tw-z-10">
            {fullscreenIndex + 1} / {totalSlides}
          </span>

          {/* Navigation */}
          {totalSlides > 1 && (
            <>
              <button
                className="tw-absolute tw-left-4 tw-top-1/2 -tw-translate-y-1/2 tw-p-3 tw-rounded-full tw-bg-gray-800 tw-text-white hover:tw-bg-gray-700 tw-transition-colors tw-z-10"
                onClick={fullscreenPrev}
              >
                <ChevronLeft className="tw-w-5 tw-h-5" />
              </button>
              <button
                className="tw-absolute tw-right-4 tw-top-1/2 -tw-translate-y-1/2 tw-p-3 tw-rounded-full tw-bg-gray-800 tw-text-white hover:tw-bg-gray-700 tw-transition-colors tw-z-10"
                onClick={fullscreenNext}
              >
                <ChevronRight className="tw-w-5 tw-h-5" />
              </button>
            </>
          )}

          {/* Content */}
          {isSlide3D(fullscreenIndex) ? (
            <div className="tw-w-full tw-h-full" onClick={e => e.stopPropagation()}>
              <Suspense
                fallback={
                  <div className="tw-w-full tw-h-full tw-flex tw-items-center tw-justify-center tw-text-sm tw-text-white">
                    Chargement de la visionneuse 3D…
                  </div>
                }
              >
                <Brochure3D
                  images={getBrochure3DImages()}
                  displayControls={true}
                  isFullscreen={true}
                  type={getBrochure3DType()}
                  onFullscreenRequest={closeFullscreen}
                  productName={product.name}
                />
              </Suspense>
            </div>
          ) : (
            <img
              src={(() => {
                const url = getImageForSlide(fullscreenIndex);
                return url?.startsWith('http') ? url : getImageUrl(url || '');
              })()}
              alt={`${product.name} - Plein écran`}
              className="tw-max-h-[90vh] tw-max-w-[90vw] tw-object-contain"
              onClick={closeFullscreen}
            />
          )}
        </div>
      )}

      {/* Pricing tiers modal */}
      {showPricingModal && (
        <PricingTiersModal product={product} onClose={() => setShowPricingModal(false)} />
      )}
    </>
  );
}
